import { useState, useEffect } from "react";
import { ArrowLeft, Calendar, Phone, UserCircle, CheckCircle2, Pencil } from "lucide-react";
import { Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { dbStore } from "@/db/offlineDb";
import { usePOS } from "@/context/POSContext";
import { useCaptainAllTablesState } from "@/hooks/useCaptainOperationalState";
import { NetworkIndicator } from "@/components/captain/NetworkIndicator";
import { meshTablesPath, meshMenuPath } from "@/lib/captainMesh/routes";
import { saveCustomerDraft, loadCustomerDraft, clearCustomerDraft } from "@/lib/captainMesh/customerDraft";
import { customerSkipAllowed, draftIdentifiesGuest } from "@/lib/captainMesh/customerSkip";

const MIN_SEARCH_CHARS = 2;
const MAX_MATCHES = 6;

/**
 * The Customer step — a two-phase form, not a data-entry gate.
 *
 * Phase "identify": Name + Mobile only. Both fields ARE the search — matches
 * from the local cache and the server surface as they type, and picking one
 * skips everything else. DOB and Anniversary appear only AFTER the guest is
 * identified (phase "details"), and neither blocks the workflow: they exist for
 * birthday/anniversary offers, and a waiter with a table waiting is not a data
 * collector. The old form demanded DOB up front, which was exactly backwards.
 *
 * The draft is table-scoped sessionStorage (§14): entered details survive
 * Customer → Menu → Home → reopen — the loop that used to wipe them — and
 * nothing persists anywhere authoritative until the first item creates the
 * order.
 *
 * `?edit=1` is the long-press "Edit Customer" path for a table that already
 * has an order: the §7 guard below would otherwise bounce an occupied table to
 * the menu. In edit mode, saving emits CUSTOMER_ASSOCIATED on the aggregate —
 * the real domain event, last-write-wins at reconciliation.
 */
export default function CaptainCustomer() {
  const { tableId } = useParams();
  const [params] = useSearchParams();
  const editMode = params.get("edit") === "1";
  const navigate = useNavigate();
  const { token, tables, settings, user } = usePOS();
  const canSkip = customerSkipAllowed(settings, user);
  const { orderForTable } = useCaptainAllTablesState();
  const table = tables?.find(t => t.id === parseInt(tableId) || t.id === tableId);
  const runningOrder = table ? orderForTable(table.id) : null;

  const draft = loadCustomerDraft(tableId);
  const editSeed = editMode ? runningOrder?.customer : null;
  const seed = editSeed || draft;

  const [phase, setPhase] = useState(seed?.name && seed?.phone ? "details" : "identify");
  const [formData, setFormData] = useState({
    id: seed?.id ?? null,
    name: seed?.name || "",
    phone: seed?.phone || "",
    dob: seed?.dob || "",
    anniversary: seed?.anniversary || "",
  });
  const [matches, setMatches] = useState([]);
  const [editingDetails, setEditingDetails] = useState(!(seed?.dob || seed?.anniversary));

  // The projection arrives a beat after mount (liveQuery), so edit mode cannot
  // seed from useState initialisers alone - a long-pressed Edit Customer opened
  // a BLANK form for a table with a guest. Seed when the order lands.
  useEffect(() => {
    if (!editMode) return;
    const guest = runningOrder?.customer;
    if (guest && !formData.name && !formData.phone) {
      setFormData({
        id: guest.id ?? null,
        name: guest.name || "",
        phone: guest.phone || "",
        dob: guest.dob || "",
        anniversary: guest.anniversary || "",
      });
      setEditingDetails(!(guest.dob || guest.anniversary));
      setPhase("details");
    }
  }, [editMode, runningOrder, formData.name, formData.phone]);

  const nameTerm = formData.name.trim();
  const phoneTerm = formData.phone.trim();
  const phoneValid = /^\d{10}$/.test(phoneTerm);

  // Live search while identifying: local cache first (works offline), then the
  // server, where the customers actually live. Merged, deduped by id/phone.
  useEffect(() => {
    if (phase !== "identify") return undefined;
    const byName = nameTerm.length >= MIN_SEARCH_CHARS;
    const byPhone = phoneTerm.length >= MIN_SEARCH_CHARS;
    if (!byName && !byPhone) {
      setMatches([]);
      return undefined;
    }
    let stale = false;
    const lowerName = nameTerm.toLowerCase();

    const localSearch = dbStore("customers").toArray()
      .then(all => all.filter(c =>
        (byName && c.name && c.name.toLowerCase().includes(lowerName)) ||
        (byPhone && c.phone && String(c.phone).startsWith(phoneTerm))
      ))
      .catch(() => []);

    const qs = new URLSearchParams();
    if (byPhone) qs.set("phone", phoneTerm);
    else if (byName) qs.set("name", nameTerm);
    const serverSearch = token
      ? fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customers/?${qs}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
          .then(r => (r.ok ? r.json() : []))
          .catch(() => [])
      : Promise.resolve([]);

    Promise.all([localSearch, serverSearch]).then(([local, remote]) => {
      if (stale) return;
      const seen = new Set();
      const merged = [];
      for (const c of [...local, ...remote]) {
        const key = c.id ?? c.phone;
        if (key != null && seen.has(key)) continue;
        if (key != null) seen.add(key);
        merged.push(c);
      }
      setMatches(merged.slice(0, MAX_MATCHES));
    });

    return () => { stale = true; };
  }, [phase, nameTerm, phoneTerm, token]);

  const back = () => navigate(meshTablesPath());

  const selectCustomer = customer => {
    const record = {
      id: customer.id,
      name: customer.name,
      phone: customer.phone,
      dob: customer.dob || "",
      anniversary: customer.anniversary || "",
    };
    setFormData(record);
    setEditingDetails(false);
    if (editMode) {
      setPhase("details");
      return;
    }
    // Skip off: the Menu refuses a new table whose guest lacks a name and a
    // 10-digit mobile, so a stored record that fails that (e.g. "+91 98765
    // 43210") must be corrected HERE - forwarding it bounced Menu -> Customer
    // -> Menu forever.
    if (!canSkip && !draftIdentifiesGuest(record)) {
      setFormData(f => ({ ...f, phone: String(record.phone || "").replace(/\D/g, "").slice(-10) }));
      setPhase("identify");
      toast.error(`Check ${record.name || "this guest"}'s details - a name and a 10-digit mobile are needed`);
      return;
    }
    // A known guest goes straight to the menu — no DOB re-interrogation.
    toMenu(record);
  };

  // sessionStorage is the draft's home (survives reload), but it can be full or
  // blocked; the guest also rides the navigation so the Menu still has it.
  const toMenu = record => {
    saveCustomerDraft(tableId, record);
    navigate(meshMenuPath(tableId), { state: { guest: record } });
  };

  const identify = e => {
    e.preventDefault();
    if (!nameTerm || !phoneValid) return;
    if (!formData.id) {
      // A typed identity is a NEW guest: dates from a previously selected or
      // drafted guest must not ride along onto them.
      setFormData(f => ({ ...f, dob: "", anniversary: "" }));
      setEditingDetails(true);
    }
    setPhase("details");
  };

  const continueToMenu = async e => {
    e.preventDefault();
    const record = { ...formData, name: nameTerm, phone: phoneTerm };
    if (editMode && runningOrder) {
      const { meshService } = await import("@/lib/captainMesh/application/meshService");
      const { EventTypes } = await import("@/lib/captainMesh/domain/events");
      await meshService.submitEvent(runningOrder.order_id, EventTypes.CUSTOMER_ASSOCIATED, { customer: record });
      toast.success(`Guest updated for ${table?.name}`);
      navigate(meshTablesPath());
      return;
    }
    if (!canSkip && !draftIdentifiesGuest(record)) {
      setPhase("identify");
      toast.error("A name and a 10-digit mobile are needed before ordering");
      return;
    }
    toMenu(record);
  };

  const skip = () => {
    clearCustomerDraft(tableId);
    navigate(meshMenuPath(tableId));
  };

  // §7 guard: an occupied table skips Customer — unless this is explicitly the
  // Edit Customer flow, which exists FOR occupied tables.
  if (runningOrder && !editMode) {
    return <Navigate to={meshMenuPath(tableId)} replace />;
  }

  const fieldClass =
    "w-full h-12 pl-10 pr-4 rounded-lg border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary";
  const labelClass = "block text-meta font-bold text-muted-foreground mb-tight uppercase";

  return (
    <div className="flex flex-col h-full bg-background relative animate-slide-up" data-testid="captain-customer-screen">
      <header className="min-h-[3.5rem] pt-safe flex items-center justify-between px-3 bg-primary text-primary-foreground shadow-md sticky top-0 z-20 shrink-0">
        <div className="flex items-center gap-2">
          <button
            className="w-11 h-11 -ml-2 flex items-center justify-center rounded-full hover:bg-primary-foreground/20 transition-colors"
            aria-label="Back to tables"
            onClick={back}
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div className="flex flex-col">
            <small className="text-micro font-bold opacity-80 uppercase">{editMode ? "Edit guest" : table?.name}</small>
            <h1 className="text-body font-bold tracking-wide leading-tight">Customer</h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <NetworkIndicator />
          {!editMode && canSkip && (
            <button
              className="h-11 px-3 text-body font-bold hover:bg-primary-foreground/10 rounded-md"
              onClick={skip}
              aria-label="Walk-in — continue without guest details"
            >
              Skip
            </button>
          )}
        </div>
      </header>

      <div className="flex-1 overflow-y-auto p-base bg-muted/10">
        {phase === "identify" && (
          <form className="space-y-base bg-card p-loose rounded-xl border shadow-sm" onSubmit={identify}>
            <div>
              <label className={labelClass}>Name *</label>
              <div className="relative">
                <UserCircle className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/60" />
                <input
                  required
                  data-testid="customer-name-input"
                  className={fieldClass}
                  value={formData.name}
                  onChange={e => setFormData(f => ({ ...f, name: e.target.value, id: null }))}
                />
              </div>
            </div>
            <div>
              <label className={labelClass}>Mobile *</label>
              <div className="relative">
                <Phone className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/60" />
                <input
                  required
                  type="tel"
                  inputMode="numeric"
                  placeholder="10 digits"
                  data-testid="customer-phone-input"
                  className={fieldClass}
                  value={formData.phone}
                  onChange={e =>
                    setFormData(f => ({ ...f, phone: e.target.value.replace(/\D/g, "").slice(0, 10), id: null }))
                  }
                />
              </div>
            </div>

            {matches.length > 0 && (
              <div className="border rounded-xl divide-y overflow-hidden" data-testid="customer-matches">
                <div className="px-3 py-2 bg-muted/30 text-micro font-bold uppercase text-muted-foreground">
                  Customer found
                </div>
                {matches.map(m => (
                  <button
                    type="button"
                    key={m.id ?? m.phone}
                    className="w-full text-left p-3 hover:bg-muted/40 transition-colors flex items-center gap-3"
                    onClick={() => selectCustomer(m)}
                    data-testid="use-customer-button"
                  >
                    <div className="flex-1 min-w-0">
                      <strong className="block text-body text-foreground truncate">{m.name}</strong>
                      <span className="flex items-center gap-loose text-meta text-muted-foreground">
                        <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{m.phone}</span>
                        {m.dob && <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{m.dob}</span>}
                      </span>
                    </div>
                    <span className="text-primary text-meta font-bold shrink-0">Use ›</span>
                  </button>
                ))}
              </div>
            )}

            <button
              type="submit"
              data-testid="customer-continue-button"
              disabled={!nameTerm || !phoneValid}
              className="w-full h-12 bg-action text-action-foreground rounded-lg font-bold text-body tracking-wide hover:bg-action/90 transition-colors disabled:bg-muted disabled:text-muted-foreground disabled:shadow-none"
            >
              Continue
            </button>
          </form>
        )}

        {phase === "details" && (
          <form className="space-y-base" onSubmit={continueToMenu}>
            <div className="bg-card p-base rounded-xl border shadow-sm flex items-center gap-3" data-testid="customer-identified">
              <CheckCircle2 className="w-6 h-6 text-success shrink-0" />
              <div className="flex-1 min-w-0">
                <strong className="block text-body font-bold text-foreground truncate">{nameTerm}</strong>
                <span className="text-meta text-muted-foreground">{phoneTerm}</span>
              </div>
              <button
                type="button"
                className="w-11 h-11 flex items-center justify-center rounded-md hover:bg-muted text-muted-foreground"
                aria-label="Change guest"
                data-testid="customer-change-button"
                onClick={() => setPhase("identify")}
              >
                <Pencil className="w-4 h-4" />
              </button>
            </div>

            <div className="bg-card p-loose rounded-xl border shadow-sm space-y-base">
              {!editingDetails ? (
                <div className="flex items-center justify-between">
                  <div className="text-meta text-muted-foreground space-y-1">
                    <div>DOB: {formData.dob || "—"}</div>
                    <div>Anniversary: {formData.anniversary || "—"}</div>
                  </div>
                  <button
                    type="button"
                    className="h-11 px-3 rounded-md text-primary text-meta font-bold hover:bg-primary/10"
                    onClick={() => setEditingDetails(true)}
                  >
                    Edit details
                  </button>
                </div>
              ) : (
                <>
                  {/* Both optional (spec §10): they drive birthday/anniversary
                      offers, and neither may hold up seating a table. */}
                  <div>
                    <label className={labelClass}>Date of Birth (Optional)</label>
                    <div className="relative">
                      <Calendar className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/60" />
                      <input
                        type="date"
                        data-testid="customer-dob-input"
                        className={fieldClass}
                        value={formData.dob}
                        onChange={e => setFormData(f => ({ ...f, dob: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div>
                    <label className={labelClass}>Anniversary (Optional)</label>
                    <div className="relative">
                      <Calendar className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/60" />
                      <input
                        type="date"
                        data-testid="customer-anniversary-input"
                        className={fieldClass}
                        value={formData.anniversary}
                        onChange={e => setFormData(f => ({ ...f, anniversary: e.target.value }))}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>

            <button
              type="submit"
              data-testid="customer-continue-button"
              className="w-full h-12 bg-action text-action-foreground rounded-lg font-bold text-body tracking-wide hover:bg-action/90 transition-colors"
            >
              {editMode ? "Save guest" : "Continue to Menu"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
